/**
 * @file 本だなのページ画像の共通部品 (遅延読み + タップで全画面)。
 *
 * @what: LazyPageImage (画面に入ってから取得する遅延読みの <img> ボタン) と
 *   SourcePageLightbox (FullscreenViewer での拡大)。うんどうタブの遊びカード・
 *   週メニューのページ内表示が共用する。遅延の判定は hooks/useInView。
 * @why: サムネイルや誌面の埋め込みを増やすたびに IntersectionObserver と LRU の扱いを
 *   書き直さないため (画像はビューワと同じ pageImageCache に乗る)。
 * @failure: IntersectionObserver が無い環境 (テスト等) では最初から取得する。取得失敗は
 *   画像が出ないだけ (枠は残る)。
 */

import type { StudyPage } from "../api/pages";
import { useInView } from "../hooks/useInView";
import { useScanImage } from "../hooks/useScanImage";
import { FullscreenViewer } from "./FullscreenViewer";

/** 遅延読みのページ画像。ボタンとして描き、タップで onOpen (拡大など) を呼ぶ。 */
export function LazyPageImage({
  page,
  label,
  className,
  onOpen,
}: {
  page: StudyPage;
  label: string;
  className: string;
  onOpen: () => void;
}) {
  const [ref, inView] = useInView<HTMLButtonElement>();
  const image = useScanImage(inView ? page.image_path : null, page.content_type);
  return (
    <button
      ref={ref}
      type="button"
      className={className}
      aria-label={`${label}を拡大する`}
      title="タップで拡大"
      onClick={onOpen}
    >
      {image.status === "ready" ? <img src={image.url} alt="" loading="lazy" /> : null}
    </button>
  );
}

/** 拡大表示。開いてから画像を引く (遅延読みと同じキャッシュに乗るので通常は即時)。 */
export function SourcePageLightbox({
  page,
  label,
  onClose,
}: {
  page: StudyPage;
  label: string;
  onClose: () => void;
}) {
  const image = useScanImage(page.image_path, page.content_type);
  return (
    <FullscreenViewer image={image} alt={label} label={`${label}（p.${page.page}）`} onClose={onClose} />
  );
}
